import React, { useEffect, useReducer, useState } from "react";
import { useHistory } from "react-router-dom";
import openSocket from "../../services/socket-io";

import {
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import RefreshIcon from "@material-ui/icons/Refresh";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import PixelManualSearch from "../../components/PixelManualSearch";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { getPixelCreativeName } from "../../utils/pixelCreative";

const reducer = (state, action) => {
  if (action.type === "LOAD") {
    const items = action.payload;
    const newItems = [];

    items.forEach((item) => {
      const index = state.findIndex((entry) => entry.id === item.id);
      if (index !== -1) {
        state[index] = item;
      } else {
        newItems.push(item);
      }
    });

    return [...state, ...newItems];
  }

  if (action.type === "UPDATE") {
    const item = action.payload;
    const index = state.findIndex((entry) => entry.id === item.id);
    if (index !== -1) {
      state[index] = item;
      return [...state];
    }
    return [item, ...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  return state;
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  messageCell: {
    maxWidth: 280,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  creativeCell: {
    maxWidth: 180,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  statsRow: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  statCard: {
    padding: theme.spacing(1.5, 2),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.default,
    minWidth: 140,
  },
}));

const renderStatusChip = (tracking) => {
  if (tracking.converted) {
    return (
      <Chip
        size="small"
        color="primary"
        label={i18n.t("pixel.status.convertedShort")}
      />
    );
  }

  if (tracking.found && tracking.pixelComplete) {
    return (
      <Chip
        size="small"
        style={{ backgroundColor: "#10b981", color: "#fff" }}
        label={i18n.t("pixel.status.complete")}
      />
    );
  }

  if (tracking.found) {
    return (
      <Chip
        size="small"
        style={{ backgroundColor: "#f59e0b", color: "#fff" }}
        label={i18n.t("pixel.status.incomplete")}
      />
    );
  }

  return (
    <Chip size="small" color="secondary" label={i18n.t("pixel.status.notFound")} />
  );
};

const Pixel = () => {
  const classes = useStyles();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [pixelTrackings, dispatch] = useReducer(reducer, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchPixelTrackings = async () => {
        try {
          const { data } = await api.get("/pixel", {
            params: { searchParam, pageNumber },
          });
          dispatch({ type: "LOAD", payload: data.pixelTrackings });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchPixelTrackings();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const socket = openSocket();
    if (!socket) return undefined;

    socket.on("pixelTracking", (data) => {
      if (data.action === "update" && data.pixelTracking) {
        dispatch({ type: "UPDATE", payload: data.pixelTracking });
      }
    });

    return () => {
      socket.off("pixelTracking");
    };
  }, []);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const loadMore = () => {
    setPageNumber((prev) => prev + 1);
  };

  const handleOpenTicket = (ticketId) => {
    history.push(`/tickets/${ticketId}`);
  };

  const completeCount = pixelTrackings.filter(
    (item) => item.found && item.pixelComplete && !item.converted
  ).length;
  const convertedCount = pixelTrackings.filter((item) => item.converted).length;
  const missingCount = pixelTrackings.filter(
    (item) => item.found && !item.pixelComplete
  ).length;

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("pixel.title")}</Title>
        <MainHeaderButtonsWrapper>
          <TextField
            placeholder={i18n.t("pixel.search")}
            type="search"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "gray" }} />
                </InputAdornment>
              ),
            }}
          />
          <IconButton onClick={() => setPageNumber(1)}>
            <RefreshIcon />
          </IconButton>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <PixelManualSearch />

      <div className={classes.statsRow}>
        <div className={classes.statCard}>
          <Typography variant="caption">{i18n.t("pixel.stats.total")}</Typography>
          <Typography variant="h6">{pixelTrackings.length}</Typography>
        </div>
        <div className={classes.statCard}>
          <Typography variant="caption">{i18n.t("pixel.stats.complete")}</Typography>
          <Typography variant="h6">{completeCount}</Typography>
        </div>
        <div className={classes.statCard}>
          <Typography variant="caption">{i18n.t("pixel.stats.incomplete")}</Typography>
          <Typography variant="h6">{missingCount}</Typography>
        </div>
        <div className={classes.statCard}>
          <Typography variant="caption">{i18n.t("pixel.stats.converted")}</Typography>
          <Typography variant="h6">{convertedCount}</Typography>
        </div>
      </div>

      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">{i18n.t("pixel.table.contact")}</TableCell>
              <TableCell align="center">{i18n.t("pixel.table.message")}</TableCell>
              <TableCell align="center">{i18n.t("pixel.table.creative")}</TableCell>
              <TableCell align="center">{i18n.t("pixel.table.phone")}</TableCell>
              <TableCell align="center">{i18n.t("pixel.table.status")}</TableCell>
              <TableCell align="center">{i18n.t("pixel.table.checkedAt")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {pixelTrackings.map((tracking) => (
                <TableRow
                  key={tracking.id}
                  hover
                  style={{ cursor: "pointer" }}
                  onClick={() => handleOpenTicket(tracking.ticketId)}
                >
                  <TableCell align="center">
                    {tracking.contact?.name || "—"}
                  </TableCell>
                  <TableCell align="center" className={classes.messageCell}>
                    {tracking.message || "—"}
                  </TableCell>
                  <TableCell align="center" className={classes.creativeCell}>
                    {getPixelCreativeName(tracking) || "—"}
                  </TableCell>
                  <TableCell align="center">
                    {tracking.customerPhone || tracking.contact?.number || "—"}
                  </TableCell>
                  <TableCell align="center">{renderStatusChip(tracking)}</TableCell>
                  <TableCell align="center">
                    {tracking.checkedAt
                      ? new Date(tracking.checkedAt).toLocaleString("pt-PT")
                      : "—"}
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={6} />}
            </>
          </TableBody>
        </Table>
        {hasMore && !loading && (
          <div style={{ textAlign: "center", marginTop: 16 }}>
            <Typography
              variant="body2"
              color="primary"
              style={{ cursor: "pointer" }}
              onClick={loadMore}
            >
              {i18n.t("pixel.loadMore")}
            </Typography>
          </div>
        )}
      </Paper>
    </MainContainer>
  );
};

export default Pixel;
